/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useEffect, useMemo, useReducer, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography } from '@mui/material';

import { IMenuItem, IReducerAction } from '@dw/models/shared';
import { Button, Form, FormDateInput, FormSelectInput, FormTextInput } from '@dw/components/core';

type TSnackbarActions = 'OPEN' | 'CLOSE';

export interface IConfirmCaptureInputConfig {
    label: string;
    inputType?: 'text' | 'select' | 'date' | 'email' | 'number';
    multiline?: boolean;
    rows?: number;
    required?: boolean;
    maxLength?: number;
    helperText?: string;
    options?: IMenuItem[];
    defaultValue?: any;
}

interface IConfirmConfig<T = Record<string, any>> {
    title?: string;
    message?: string;
    body?: React.ReactNode;
    onPositive?: (capturedInput?: T) => void | Promise<void>;
    positiveText?: string;
    positiveOnly?: boolean;
    onNegative?: () => void | Promise<void>;
    negativeText?: string;
    captureInputConfigs?: Record<string, IConfirmCaptureInputConfig>;
}

interface IConfirmContext {
    confirm: <T = Record<string, any>>(config: IConfirmConfig<T>) => void;
    isOpen: boolean;
    config: IConfirmConfig | null;
}

const initialState: IConfirmContext = {
    confirm: () => null,
    isOpen: false,
    config: null
};
export const ConfirmContext = React.createContext<IConfirmContext>(initialState);

const ConfirmContextReducer = (state: IConfirmContext, action: IReducerAction<TSnackbarActions>): IConfirmContext => {
    switch (action.type) {
        case 'OPEN':
            return {...state, isOpen: true, config: action.payload};
        case 'CLOSE':
            return {...state, isOpen: false, config: null};
        default:
            return state;
    }
};

interface IConfirmProviderProps {
    children: React.ReactNode;
};
export const ConfirmContextProvider: FunctionComponent<IConfirmProviderProps> = (props: IConfirmProviderProps) => {
    const [state, dispatch] = useReducer(ConfirmContextReducer, {...initialState,
        confirm: (config: IConfirmConfig<any>) => {
            dispatch({
                type: 'OPEN',
                payload: config
            });
        }
    });

    const [captureInputFormData, setCaptureInputFormData] = useState<Record<string, any>>();
    const shouldCaptureInputs = useMemo(() => Object.keys(state.config?.captureInputConfigs ?? {}).length > 0, [state.config?.captureInputConfigs]);

    const handleNegativeClose = async () => {
        if (state.config?.onNegative) {
            await Promise.resolve(state.config.onNegative());
        }
        dispatch({ type: 'CLOSE' });
    }

    const handlePositive = async () => {
        if (state.config?.onPositive) {
            const capturedInput = shouldCaptureInputs ? captureInputFormData : undefined;
            await Promise.resolve(state.config.onPositive(capturedInput));
        }
        dispatch({type: 'CLOSE'});
    }

    useEffect(() => {
        // Reset the form data when the dialog opens
        setCaptureInputFormData(
            Object.keys(state.config?.captureInputConfigs ?? {}).reduce((acc, key) => {
                if (state.config?.captureInputConfigs![key].defaultValue) {
                    acc[key] = state.config?.captureInputConfigs![key].defaultValue;
                }
                return acc;
            }, {} as Record<string, any>)
        );
    }, [state.isOpen, state.config?.captureInputConfigs]);

    return (
        <ConfirmContext.Provider value={state}>
            {props.children}

            <Dialog
                open={state.isOpen}
                onClose={handleNegativeClose}
            >
                <Form
                    formData={captureInputFormData}
                    formDataChanged={setCaptureInputFormData}
                    onSubmit={handlePositive}
                >
                    <DialogTitle>{state.config?.title ?? 'Are you sure?'}</DialogTitle>
                    <DialogContent>
                        {state.config?.message && (
                            <Typography variant='body2' sx={{
                                whiteSpace: 'break-spaces'
                            }}>
                                {state.config?.message}
                            </Typography>
                        )}

                        {state.config?.body}

                        {shouldCaptureInputs && (
                            <Stack marginTop={2}>
                                {Object.keys(state.config!.captureInputConfigs ?? {}).map((key) => state.config!.captureInputConfigs![key].inputType === 'select' ? (
                                    <FormSelectInput
                                        key={key}
                                        name={key}
                                        options={state.config!.captureInputConfigs![key].options ?? []}
                                        label={state.config!.captureInputConfigs![key].label}
                                        required={state.config!.captureInputConfigs![key].required}
                                        helperText={state.config!.captureInputConfigs![key].helperText}
                                    />
                                ) : state.config!.captureInputConfigs![key].inputType === 'date' ? (
                                    <FormDateInput
                                        key={key}
                                        name={key}
                                        label={state.config!.captureInputConfigs![key].label}
                                        required={state.config!.captureInputConfigs![key].required}
                                        helperText={state.config!.captureInputConfigs![key].helperText}
                                    />
                                ) : (
                                    <FormTextInput
                                        key={key}
                                        name={key}
                                        type={state.config!.captureInputConfigs![key].inputType}
                                        label={state.config!.captureInputConfigs![key].label}
                                        multiline={state.config!.captureInputConfigs![key].multiline}
                                        minRows={state.config!.captureInputConfigs![key].rows}
                                        maxRows={state.config!.captureInputConfigs![key].rows}
                                        required={state.config!.captureInputConfigs![key].required}
                                        maxLength={state.config!.captureInputConfigs![key].maxLength}
                                        helperText={state.config!.captureInputConfigs![key].helperText}
                                    />
                                ))}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {
                            !(state.config?.positiveOnly) && (
                                <Button
                                    variant='text'
                                    color='error'
                                    type='button'
                                    onClick={handleNegativeClose}
                                >
                                    {state.config?.negativeText ?? 'Cancel'}
                                </Button>
                            )
                        }

                        <Button
                            variant='text'
                            type={shouldCaptureInputs ? 'submit' : 'button'}
                            onClick={() => !shouldCaptureInputs ? handlePositive() : null}
                        >
                            {state.config?.positiveText ?? 'Continue'}
                        </Button>
                    </DialogActions>
                </Form>
            </Dialog>
        </ConfirmContext.Provider>
    );
}